import type {
  DeadlineCertainty,
  RegulatoryRecord,
  TimelineEvent,
  TimelineEventType,
  TimelineKind,
} from '../../data/regulations/types'
import { compareIso, todayIsoInUae } from './dates'

function kindFor(input: {
  eventType: TimelineEventType
  certainty: DeadlineCertainty | 'undated'
  date?: string
  status: RegulatoryRecord['status']
  today: string
}): TimelineKind {
  if (input.status === 'repealed' || input.eventType === 'repeal') return 'repealed'
  if (input.eventType === 'amendment') return 'amended'
  if (input.eventType === 'consultation') return 'consultation'
  if (input.certainty === 'expected' || input.eventType === 'expected') return 'expected'
  if (input.certainty === 'proposed') return 'proposed'
  if (!input.date) return input.status === 'historical' ? 'historical' : 'historical'
  if (input.date > input.today && input.certainty === 'confirmed') return 'confirmed_future'
  if (
    input.date <= input.today &&
    input.eventType === 'effective' &&
    (input.status === 'in_force' || input.status === 'amended')
  ) {
    return 'in_force'
  }
  return 'historical'
}

export function buildTimeline(records: RegulatoryRecord[], today: string = todayIsoInUae()): TimelineEvent[] {
  const events: TimelineEvent[] = []

  for (const record of records) {
    const seenDates = new Set<string>()
    for (const deadline of record.deadlines) {
      let eventType: TimelineEventType = deadline.type
      if (
        deadline.date &&
        record.publicationDate &&
        deadline.date === record.publicationDate &&
        deadline.date !== record.effectiveDate &&
        deadline.type === 'effective'
      ) {
        eventType = 'publication'
      }
      if (deadline.date) seenDates.add(`${eventType}:${deadline.date}`)
      events.push({
        id: `${record.id}:${eventType}:${deadline.date || deadline.label}`,
        recordId: record.id,
        slug: record.slug,
        title: record.title,
        shortTitle: record.shortTitle,
        date: deadline.date,
        label: deadline.label,
        eventType,
        certainty: deadline.certainty,
        kind: kindFor({
          eventType,
          certainty: deadline.certainty,
          date: deadline.date,
          status: record.status,
          today,
        }),
        jurisdiction: record.jurisdiction,
        categories: record.categories,
        status: record.status,
        authority: record.authority,
        summary: record.summary,
        sourceUrl: deadline.sourceUrl || record.officialSources[0]?.url,
        notes: deadline.notes,
      })
    }

    if (record.publicationDate && !seenDates.has(`publication:${record.publicationDate}`) && !record.deadlines.some((d) => d.date === record.publicationDate)) {
      events.push(baseEvent(record, record.publicationDate, 'publication', 'Publication date', 'confirmed', today))
    }
    if (record.effectiveDate && !record.deadlines.some((d) => d.date === record.effectiveDate)) {
      events.push(baseEvent(record, record.effectiveDate, 'effective', 'Effective date', 'confirmed', today))
    }
    for (const amendment of record.amendments) {
      if (!amendment.date) continue
      if (record.deadlines.some((d) => d.date === amendment.date)) continue
      events.push({
        ...baseEvent(record, amendment.date, 'amendment', amendment.label, 'confirmed', today),
        sourceUrl: amendment.sourceUrl,
      })
    }
  }

  events.sort((a, b) => {
    const byDate = compareIso(a.date, b.date)
    if (byDate !== 0) return byDate
    return a.title.localeCompare(b.title)
  })
  return events
}

function baseEvent(
  record: RegulatoryRecord,
  date: string,
  eventType: TimelineEventType,
  label: string,
  certainty: DeadlineCertainty,
  today: string,
): TimelineEvent {
  return {
    id: `${record.id}:${eventType}:${date}`,
    recordId: record.id,
    slug: record.slug,
    title: record.title,
    shortTitle: record.shortTitle,
    date,
    label,
    eventType,
    certainty,
    kind: kindFor({ eventType, certainty, date, status: record.status, today }),
    jurisdiction: record.jurisdiction,
    categories: record.categories,
    status: record.status,
    authority: record.authority,
    summary: record.summary,
    sourceUrl: record.officialSources[0]?.url,
  }
}

export type TimelineFilter = {
  search?: string
  period?: 'past' | 'current' | 'future' | 'all'
  categories?: string[]
  jurisdictions?: string[]
  kinds?: TimelineKind[]
  statuses?: string[]
}

export function filterTimeline(events: TimelineEvent[], filter: TimelineFilter, today: string = todayIsoInUae()): TimelineEvent[] {
  const year = Number(today.slice(0, 4))
  const search = (filter.search || '').trim().toLowerCase()
  return events.filter((event) => {
    if (search && !`${event.title} ${event.label} ${event.authority}`.toLowerCase().includes(search)) return false
    if (filter.categories?.length && !filter.categories.some((id) => event.categories.includes(id as never))) return false
    if (filter.jurisdictions?.length && !filter.jurisdictions.some((id) => event.jurisdiction.includes(id as never))) return false
    if (filter.kinds?.length && !filter.kinds.includes(event.kind)) return false
    if (filter.statuses?.length && !filter.statuses.includes(event.status)) return false
    if (filter.period === 'past') return Boolean(event.date && event.date.slice(0, 4) < String(year))
    if (filter.period === 'current') return Boolean(event.date && event.date.slice(0, 4) === String(year))
    if (filter.period === 'future') return Boolean(event.date && event.date > today)
    return true
  })
}

export function groupTimelineByYear(events: TimelineEvent[]): { year: string; events: TimelineEvent[] }[] {
  const groups = new Map<string, TimelineEvent[]>()
  for (const event of events) {
    const year = event.date ? event.date.slice(0, 4) : 'No confirmed date'
    const list = groups.get(year) || []
    list.push(event)
    groups.set(year, list)
  }
  return [...groups.entries()].map(([year, grouped]) => ({ year, events: grouped }))
}
